import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, Radio } from "lucide-react";
import LiveStreamPlayer from "./LiveStreamPlayer";
import { SiteWatchBadge, YoutubeWatchButton } from "./WatchActions";
import { getYoutubeEmbed } from "../lib/api";
import { getLiveViewerCount } from "../lib/liveViewers";
import { useLivePresence } from "../hooks/useLiveViewers";

export default function LivePlayerModal({ open, onClose, stream }) {
  const embed = stream?.embedUrl ? getYoutubeEmbed(stream.embedUrl, true) : null;
  const showSitePlayer = Boolean(embed);
  const presenceViewers = useLivePresence(stream?.id, open && showSitePlayer);

  if (!stream) return null;

  const displayViewers = presenceViewers || getLiveViewerCount(stream);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl rounded-2xl overflow-hidden border border-[rgba(168,85,247,0.35)] bg-[#0a0b16] shadow-[0_30px_90px_-20px_rgba(139,92,246,0.6)]"
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black/90 flex items-center justify-center text-white transition-colors"
            >
              <X size={20} />
            </button>

            <div className="relative aspect-video bg-black">
              {showSitePlayer ? (
                <LiveStreamPlayer stream={stream} autoplay />
              ) : (
                <LiveStreamPlayer stream={{ ...stream, embedUrl: "" }} autoplay={false} />
              )}
            </div>

            <div className="p-5 md:p-6">
              <div className="flex items-center gap-3 mb-3">
                {stream.isActive ? (
                  <span className="tv-badge-live">
                    <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> CANLI
                  </span>
                ) : (
                  <span className="tv-badge">YAYIN DIŞI</span>
                )}
                <span className="flex items-center gap-1.5 text-xs text-[var(--tv-muted)]">
                  <Eye size={13} className="text-[var(--tv-purple)]" />
                  <span className="text-white font-medium tabular-nums">{displayViewers}</span>
                  izleyici
                </span>
              </div>
              <h3 className="text-xl font-bold text-white font-display flex items-center gap-2">
                <Radio size={18} className="text-[var(--tv-purple-bright)]" /> {stream.title}
              </h3>
              <p className="text-[var(--tv-muted)] mt-2 text-sm leading-relaxed">{stream.description}</p>

              {stream.embedUrl && (
                <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-[var(--tv-border)]">
                  {showSitePlayer && <SiteWatchBadge className="!py-2 !px-4 text-xs" />}
                  <YoutubeWatchButton url={stream.embedUrl} className="!py-2 !px-4 text-xs" />
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
